import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { makeStyles } from '@material-ui/core';
import { CheckCircleOutlined } from '@material-ui/icons';
import { colorSet, useSnackbar } from '@components/Provider';
import {
  CustomizedButton,
  CustomizedDialog,
  CustomizedTypography
} from '@components/Customized';
import ProfileUpdateForm from '@components/Form/ProfileUpdateForm/ProfileUpdateForm';
import PostCodeDialog from '../Postcode/PostCodeDialog';
import useClient from '@hooks/useClient';
import {
  useRegisterQuotationUserMutation,
  useUpdateQuotationUserMutation,
  useVerifyCompanyRegistrationNumberQuery,
  useFetchMyQuotationUserLazyQuery,
  RegisterQuotationUserInput,
  CapaErrorCode,
  CapaQuotationUserBizType
} from '@generated/graphql';
import { STORAGE_ACTION } from '@utils/storage';
import useUserActionLog from '@hooks/useUserActionLog';

interface ProfileUpdateDialogProps {
  open: boolean;
  onClose: () => void;
}

const ProfileUpdateDialog: React.FC<ProfileUpdateDialogProps> = ({
  open,
  onClose
}) => {
  const [fetchMyQuotationUser] = useFetchMyQuotationUserLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const [openPostCodeDialog, setOpenPostCodeDialog] = useState<boolean>(false);
  const [bizType, setBizType] = useState<CapaQuotationUserBizType>(
    CapaQuotationUserBizType.Biz
  );
  const [address, setAddress] = useState<{ full: string; short: string }>({
    full: '',
    short: ''
  });
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { onUpdateToken, onUpdateClient } = useClient();
  const { dialogDescription, form, submitButton } = useStyles();
  const [registerQuotationUser] = useRegisterQuotationUserMutation({
    refetchQueries: ['fetchMyQuotationUser']
  });
  const [updateQuotationUser] = useUpdateQuotationUserMutation({
    refetchQueries: ['fetchMyQuotationUser']
  });

  const {
    refetch: verifyCompanyRegistrationNumber
  } = useVerifyCompanyRegistrationNumberQuery({
    skip: true
  });
  const hookFormDatas = useForm<RegisterQuotationUserInput>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });
  const {
    handleSubmit,
    formState: { isValid },
    trigger,
    setValue,
    getValues,
    setError
  } = hookFormDatas;
  const { insertButtonLog } = useUserActionLog();

  const successAction = useCallback(async () => {
    const requestPaths = ['/request', '/processmethod'];

    const { data } = await fetchMyQuotationUser();
    const isFetchUserInfoSuccess =
      data?.fetchMyQuotationUser?.result === CapaErrorCode.Success;
    const userInfo = data?.fetchMyQuotationUser?.data;
    if (isFetchUserInfoSuccess && userInfo) {
      onUpdateClient(userInfo);
    }

    if (requestPaths.includes(pathname)) {
      enqueueSnackbar(
        '필수정보 입력이 완료되었습니다! 견적요청을 이어서 진행하세요.',
        { variant: 'success' }
      );
    } else {
      enqueueSnackbar('견적요청에 필요한 필수정보가 모두 저장되었습니다!', {
        variant: 'success'
      });
    }

    localStorage.removeItem(STORAGE_ACTION.PROFILE_UPDATE_SKIPPED);
    onClose();
    return;
  }, [
    pathname,
    enqueueSnackbar,
    onClose,
    fetchMyQuotationUser,
    onUpdateClient
  ]);

  const failAction = () => {
    enqueueSnackbar('필수정보 등록에 실패했습니다. 다시 시도해주세요.', {
      variant: 'error'
    });
    return;
  };

  const checkRegistrationNumber = useCallback(async () => {
    const companyRegistrationNumber = getValues('companyRegistrationNumber');
    if (companyRegistrationNumber) {
      const verifyResponse = await verifyCompanyRegistrationNumber({
        input: { companyRegistrationNumber }
      });

      if (
        verifyResponse &&
        verifyResponse.data.verifyCompanyRegistrationNumber.result
      ) {
        switch (verifyResponse.data.verifyCompanyRegistrationNumber.result) {
          case CapaErrorCode.Success:
            return true;
          case CapaErrorCode.DuplicateCompanyRegistrationNo:
            return true;
          case CapaErrorCode.InavlidCompanyRegistrationNo:
            return setError('companyRegistrationNumber', {
              message: '등록되지 않은 사업자등록번호 입니다.'
            });
          case CapaErrorCode.ParmeterValidationError:
            return setError('companyRegistrationNumber', {
              message: '잘못된 형식입니다.'
            });
          default:
            return setError('companyRegistrationNumber', {
              message: '사업자등록번호 조회에 실패했습니다.'
            });
        }
      }

      return setError('companyRegistrationNumber', {
        message: '사업자등록번호 조회에 실패했습니다.'
      });
    } else {
      return true;
    }
  }, [getValues, setError, verifyCompanyRegistrationNumber]);

  const updateUserInfo = async (request: RegisterQuotationUserInput) => {
    const { data: updateResponse } = await updateQuotationUser({
      variables: { input: request }
    }).catch((e) => {
      return { data: undefined };
    });

    if (updateResponse?.updateQuotationUser.data) {
      const { result, data } = updateResponse.updateQuotationUser;
      if (result === CapaErrorCode.Success && data) {
        onUpdateClient(data);
        successAction();
        return;
      }
    }

    failAction();
  };

  const onSubmit = async (request: RegisterQuotationUserInput) => {
    insertButtonLog('Button_ProfileUpdateDialog_Submit');

    /* input 인터페이스에 맞게 데이터 수정 */
    if (address.full && address.short) {
      request.shortAddress = address.short;
    }
    request.bizType = bizType;
    request.companyRegistrationNumber =
      request.companyRegistrationNumber &&
      request?.companyRegistrationNumber.length > 0
        ? request.companyRegistrationNumber
        : undefined;

    /* 사업자번호 유효성 검사 */
    const registrationNumberResult = await checkRegistrationNumber();
    if (!registrationNumberResult) return;

    /* registerQuotationUser 뮤테이션 실행 */
    const { data: registResponse } = await registerQuotationUser({
      variables: { input: request }
    }).catch((e) => {
      return { data: undefined };
    });

    if (registResponse?.registerQuotationUser.result) {
      const result = registResponse?.registerQuotationUser.result;
      if (result === CapaErrorCode.Success) {
        /* 성공한 경우 반환된 토큰으로 업데이트 */
        const { accessToken, refreshToken } =
          registResponse?.registerQuotationUser?.data ?? {};
        if (accessToken && refreshToken) {
          onUpdateToken({ accessToken, refreshToken });
          onUpdateClient(request);
          successAction();
          return;
        }
      } else if (result === CapaErrorCode.QuotationUserExists) {
        /* 이미 존재하는 유저인 경우 인풋 값으로 유저정보 업데이트 */
        updateUserInfo(request);
        return;
      }
    }

    /* 성공 혹은 이미 존재하는 유저의 경우를 제외하고 모두 fail */
    failAction();
  };

  useEffect(() => {
    if (address.full && address.short) {
      setValue('fullAddress', address.full);
      trigger('fullAddress');
    }
  }, [setValue, address, trigger]);

  return (
    <CustomizedDialog
      id="profile-update"
      open={open}
      onClose={onClose}
      title="파트너 매칭 필수정보 입력"
      icon={<CheckCircleOutlined />}
      gutter={32}
    >
      <CustomizedTypography
        variant="body2"
        color="textSecondary"
        className={dialogDescription}
      >
        캐파 고객님 환영합니다! 파트너 매칭에 필요한 필수 정보를 입력해주세요.
        입력 후 견적요청을 이어서 진행하실 수 있습니다.
      </CustomizedTypography>

      <PostCodeDialog
        open={openPostCodeDialog}
        setAddress={setAddress}
        onClose={() => setOpenPostCodeDialog(false)}
      />
      <form className={form} onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...hookFormDatas}>
          <ProfileUpdateForm
            setOpenPostCodeDialog={() => setOpenPostCodeDialog(true)}
            bizType={bizType}
            setBizType={(bizType: CapaQuotationUserBizType) =>
              setBizType(bizType)
            }
          />
        </FormProvider>
        <CustomizedButton
          className={submitButton}
          variant="contained"
          color="primary"
          type="submit"
          size="large"
          disabled={!isValid}
          fullWidth
        >
          입력 완료
        </CustomizedButton>
      </form>
    </CustomizedDialog>
  );
};

const useStyles = makeStyles((theme) => ({
  dialogDescription: {
    margin: '0px 0px 32px 0px',
    wordBreak: 'break-all',
    [theme.breakpoints.down('xs')]: {
      margin: '8px 0px 24px 0px'
    }
  },
  form: {
    width: '100%'
  },
  submitButton: {
    marginTop: 32,
    '&:disabled': {
      backgroundColor: colorSet.gray200,
      color: colorSet.gray500
    }
  }
}));

export default ProfileUpdateDialog;
